import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { MapView } from "@/components/MapView";
import { TellAFriend } from "@/components/TellAFriend";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star, Calendar, Clock, Sparkles, ArrowLeft, Navigation, Heart, Lock, Mail, Phone, MessageSquare, Globe, Briefcase } from "lucide-react";
import { toast } from "sonner";

function StarRow({ value = 0, size = "w-5 h-5" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${size} ${n <= Math.round(value) ? "fill-ys-gold text-ys-gold" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [listing, setListing] = useState(null);
  const [active, setActive] = useState(0);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gated, setGated] = useState(false);
  useEffect(() => { setSaved((user?.saved_listings || []).includes(id)); }, [user, id]);
  const toggleSave = async () => {
    if (!user) { navigate("/login"); return; }
    try { const { data } = await api.post(`/listings/${id}/save`); setSaved(data.saved); await refreshUser(); toast.success(data.saved ? "Saved — we'll remind you the morning it's live" : "Removed from saved"); }
    catch { toast.error("Could not save"); }
  };

  const messageSeller = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      const { data } = await api.post("/conversations", { listing_id: id });
      navigate(`/messages?c=${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const load = async () => {
    try { const { data } = await api.get(`/listings/${id}`); setListing(data); }
    catch (err) {
      if (err.response?.status === 402) setGated(true);
      else { toast.error("Listing not found"); navigate("/browse"); }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submitRating = async () => {
    if (!user) { navigate("/login"); return; }
    if (!stars) { toast.error("Pick a star rating"); return; }
    setSubmitting(true);
    try {
      await api.post(`/listings/${id}/ratings`, { stars, comment });
      toast.success("Thanks for rating!");
      setComment(""); setStars(0); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (gated) return (
    <div className="min-h-screen bg-ys-canvas">
      <Header />
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="listing-gated">
        <div className="w-14 h-14 rounded-2xl bg-sky-100 text-ys-sky flex items-center justify-center mx-auto mb-4"><Lock className="w-7 h-7" /></div>
        <h2 className="font-display text-2xl font-extrabold">Members only</h2>
        <p className="text-slate-500 mt-2">Sign in and choose a plan to open sale listings and see full details, photos and ratings.</p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center mt-6">
          {user ? (
            <Button onClick={() => navigate("/pricing")} data-testid="gated-plans-btn" className="rounded-full bg-ys-sky hover:bg-sky-700">See plans</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => navigate("/login")} data-testid="gated-signin-btn" className="rounded-full">Sign in</Button>
              <Button onClick={() => navigate("/register")} data-testid="gated-register-btn" className="rounded-full bg-ys-sky hover:bg-sky-700">Get started</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (!listing) return <div className="min-h-screen flex items-center justify-center bg-ys-canvas"><div className="ys-spinner" /></div>;

  const photos = listing.photos || [];
  const cover = photos[active] || photos[listing.profile_photo_index] || photos[0];

  return (
    <div className="min-h-screen bg-ys-canvas">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button onClick={() => navigate("/browse")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-to-browse">
          <ArrowLeft className="w-4 h-4" /> Back to map
        </button>

        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 relative">
              {cover ? <img src={fileUrl(cover)} alt={listing.title} className="w-full h-full object-cover" data-testid="listing-cover" /> :
                <div className="w-full h-full flex items-center justify-center text-slate-300"><MapPin className="w-12 h-12" /></div>}
              {listing.highlighted && <Badge className="absolute top-3 left-3 bg-ys-gold text-white gap-1"><Sparkles className="w-3 h-3" />Highlighted</Badge>}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto">
                {photos.map((p, i) => (
                  <button key={p} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 shrink-0 ${active === i ? "border-ys-sky" : "border-transparent"}`}>
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-start justify-between gap-3">
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="listing-title">{listing.title}</h1>
            </div>
            {listing.kind === "business" && (
              <div className="mt-2" data-testid="business-badge">
                <Badge className="bg-ys-sky text-white gap-1"><Briefcase className="w-3 h-3" />Business{listing.business_type ? ` · ${listing.business_type}` : ""}</Badge>
              </div>
            )}
            <div className="flex items-center gap-2 mt-2">
              <StarRow value={listing.rating_avg} />
              <span className="text-sm text-slate-500">{listing.rating_avg || 0} · {listing.rating_count} ratings</span>
            </div>
            {(listing.seller_username || listing.seller_name) && (
              <p className="text-sm text-slate-500 mt-1" data-testid="listing-seller">Posted by <span className="font-semibold text-ys-sky">{listing.seller_username ? `@${listing.seller_username}` : listing.seller_name}</span></p>
            )}
            <p className="text-slate-600 mt-4 leading-relaxed whitespace-pre-line" data-testid="listing-description">{listing.description}</p>

            <div className="mt-5 space-y-2 text-sm">
              <p className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-ys-sky" />{listing.address}, {listing.city} {listing.zip}</p>
              {listing.kind === "business" && listing.website && (
                <a href={/^https?:\/\//.test(listing.website) ? listing.website : `https://${listing.website}`} target="_blank" rel="noopener noreferrer" data-testid="business-website-link"
                  className="inline-flex items-center gap-2 text-ys-sky font-semibold hover:underline"><Globe className="w-4 h-4" />Visit website</a>
              )}
              {listing.kind === "business" && listing.contact_phone && (
                <div><a href={`tel:${listing.contact_phone}`} data-testid="business-call-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 h-11 transition-colors"><Phone className="w-4 h-4" />Call {listing.contact_phone}</a></div>
              )}
              {listing.show_contact && (listing.contact_email || listing.contact_phone) && (
                <div className="rounded-xl bg-sky-50 border border-sky-100 p-3 !mt-3" data-testid="listing-contact">
                  <p className="text-xs font-semibold text-ys-sky mb-1.5">Contact the seller</p>
                  {listing.contact_email && <p className="flex items-center gap-2 text-slate-700"><Mail className="w-4 h-4 text-ys-sky" /><a href={`mailto:${listing.contact_email}`} className="hover:underline" data-testid="listing-contact-email-link">{listing.contact_email}</a></p>}
                  {listing.contact_phone && <p className="flex items-center gap-2 text-slate-700"><Phone className="w-4 h-4 text-ys-sky" /><a href={`tel:${listing.contact_phone}`} className="hover:underline" data-testid="listing-contact-phone-link">{listing.contact_phone}</a></p>}
                </div>
              )}
              {(listing.sale_dates || []).map((d) => (
                <p key={`${d.date}-${d.start}-${d.end}`} className="flex items-center gap-2 text-slate-700">
                  <Calendar className="w-4 h-4 text-ys-sky" />{d.date}
                  <Clock className="w-4 h-4 text-ys-sky ml-2" />{d.start}–{d.end}
                </p>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mt-6">
              <Button onClick={() => window.open(`https://www.openstreetmap.org/directions?to=${listing.lat},${listing.lng}`, "_blank")}
                data-testid="directions-btn" className="rounded-full bg-ys-sky hover:bg-sky-700 gap-2"><Navigation className="w-4 h-4" />Directions</Button>
              <Button onClick={toggleSave} variant="outline" className="rounded-full gap-2" data-testid="save-listing-btn"><Heart className={`w-4 h-4 ${saved ? "fill-red-500 text-red-500" : ""}`} />{saved ? "Saved" : "Save"}</Button>
              {(!user || user.user_id !== listing.seller_id) && (
                <Button onClick={messageSeller} variant="outline" className="rounded-full gap-2 border-ys-sky text-ys-sky hover:bg-sky-50" data-testid="message-seller-btn"><MessageSquare className="w-4 h-4" />Message seller</Button>
              )}
              <TellAFriend url={window.location.href} label="Tell a Friend" listingTitle={listing.title} />
            </div>

            <div className="mt-6 rounded-2xl overflow-hidden border border-slate-200 h-52">
              <MapView listings={[listing]} center={[listing.lat, listing.lng]} zoom={14} className="h-full w-full" />
            </div>
          </div>
        </div>

        {/* Ratings */}
        <div className="mt-10">
          <h2 className="font-display text-xl font-bold">Shopper Ratings</h2>
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-4" data-testid="rating-form">
            <p className="text-sm font-semibold mb-2">Rate this sale</p>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setStars(n)} data-testid={`rate-star-${n}`}>
                  <Star className={`w-8 h-8 ${n <= stars ? "fill-ys-gold text-ys-gold" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share what you found…"
              data-testid="rating-comment" className="mt-3 rounded-xl" />
            <Button onClick={submitRating} disabled={submitting} data-testid="submit-rating-btn" className="mt-3 rounded-full bg-ys-sky hover:bg-sky-700">
              {submitting ? "Posting…" : "Post Rating"}
            </Button>
          </div>

          <div className="mt-5 space-y-3" data-testid="ratings-list">
            {(listing.ratings || []).length === 0 && <p className="text-sm text-slate-400">No ratings yet — be the first!</p>}
            {(listing.ratings || []).map((r) => (
              <div key={r.id} className="bg-white rounded-2xl border border-slate-100 p-4">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm" data-testid="rating-author">{r.user_username ? `@${r.user_username}` : r.user_name}</p>
                  <StarRow value={r.stars} size="w-4 h-4" />
                </div>
                {r.comment && <p className="text-sm text-slate-600 mt-1.5">{r.comment}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
